import type { IAgentImportResult } from "../types";

interface IImportItemsInBatchesProps<TItem, TResult> {
  items: TItem[];
  batchSize: number;
  sendBatch: (batch: TItem[]) => Promise<TResult[]>;
  // NOTE: The row shown for an item whose batch never came back.
  toUnconfirmedResult: (item: TItem) => TResult;
}

interface IImportInBatchesProps<TAgent extends { agentId: string }> {
  agents: TAgent[];
  batchSize: number;
  sendBatch: (batch: TAgent[]) => Promise<IAgentImportResult[]>;
}

// NOTE: NSAI-6583 / NSAI-6677 — the server caps one import request, and a
// migration is often larger, so every import source sends its selection in
// sequential batches and hands back one combined result list. Sequential, not
// parallel: the server's duplicate-name check would race across concurrent
// batches. A failed first batch rethrows (nothing was confirmed); a later
// failure keeps the confirmed rows and marks the rest unconfirmed, so the user
// still sees what was already created instead of a bare error. NSAI-8009 made
// the item and row types generic for the Manus automation import.
export const importItemsInBatches = async <TItem, TResult>({
  items,
  batchSize,
  sendBatch,
  toUnconfirmedResult,
}: IImportItemsInBatchesProps<TItem, TResult>): Promise<TResult[]> => {
  const results: TResult[] = [];

  for (let start = 0; start < items.length; start += batchSize) {
    const batch = items.slice(start, start + batchSize);
    try {
      results.push(...(await sendBatch(batch)));
    } catch (error) {
      if (start === 0) {
        throw error;
      }
      return [...results, ...items.slice(start).map(toUnconfirmedResult)];
    }
  }

  return results;
};

export const importInBatches = async <TAgent extends { agentId: string }>({
  agents,
  batchSize,
  sendBatch,
}: IImportInBatchesProps<TAgent>): Promise<IAgentImportResult[]> =>
  importItemsInBatches({
    items: agents,
    batchSize,
    sendBatch,
    toUnconfirmedResult: (agent) => ({
      agentId: agent.agentId,
      success: false,
      isOutcomeUnknown: true,
    }),
  });
